/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'react-intl';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';

import type { ApiState as ApiStatus } from 'bundles/coursera-ui/constants/apiNotificationConstants';
import { EnrollmentChoiceModalBody } from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModal';
import EnrollmentChoiceModalProductDescription from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalProductDescription';
import { useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import type { NaptimeError } from 'bundles/naptimejs';
import type { ProgramMembershipType } from 'bundles/program-home/types/ProgramMembershipAdapter';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const styles = {
  container: css`
    margin: var(--cds-spacing-400) var(--cds-spacing-600) 0 var(--cds-spacing-600);
    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-200);
    }
  `,
  bodyText: css`
    margin-top: var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  strong: css`
    && {
      font-weight: bold;
    }
  `,
  buttonContainer: css`
    margin: -8px;
    ${breakpoints.down('xs')} {
      display: flex;
      flex-flow: column nowrap;
    }
  `,
  button: css`
    margin: var(--cds-spacing-100);
    ${breakpoints.down('xs')} {
      flex-grow: 1;

      && {
        max-width: initial;
      }
    }
  `,
};

type PropsFromCaller = {
  onClose: () => void;
  programName: string;
  onResendEmail: () => void;
  onCheckVerified: () => void;
  apiStatus?: ApiStatus;
  error?: NaptimeError | null;
  programMembership: ProgramMembershipType;
};

type PropsFromRouter = {
  hasProductContext?: boolean;
};

type Props = PropsFromCaller & PropsFromRouter;

export const ProgramEmailVerifyModal = ({
  programName,
  onResendEmail,
  onCheckVerified,
  apiStatus,
  error,
  onClose,
  programMembership,
  hasProductContext,
}: Props) => {
  const emailAddress = programMembership.externalUserData.definition.email;
  const busy = apiStatus === 'API_IN_PROGRESS' || apiStatus === 'API_BEFORE_SEND';

  return (
    <div className="ProgramEmailVerifyModal">
      <EnrollmentChoiceModalBody
        headerTitle={_t('Verify your email address')}
        button={
          <div css={styles.buttonContainer}>
            <Button variant="primary" css={styles.button} onClick={onResendEmail} disabled={busy}>
              {_t('Resend verification email')}
            </Button>
            <Button variant="secondary" css={styles.button} onClick={onCheckVerified} disabled={busy}>
              {_t("Yes, I'm verified")}
            </Button>
            <Button variant="ghost" css={styles.button} onClick={onClose}>
              {_t('Dismiss')}
            </Button>
          </div>
        }
      >
        <EnrollmentChoiceModalProductDescription />
        <div css={styles.container}>
          <Typography2 variant="subtitleLarge" component="h3" css={styles.bodyText}>
            {_t('Verify email to join the learning program')}
          </Typography2>
          <Typography2 component="p" css={styles.bodyText}>
            <FormattedMessage
              message={
                hasProductContext
                  ? _t(
                      'You must join {programName} to enroll through your organization. Please verify your email address first.'
                    )
                  : _t('You must verify your email address before you can join {programName}.')
              }
              programName={
                <Typography2 component="strong" css={styles.strong}>
                  {programName}
                </Typography2>
              }
            />
          </Typography2>
          <Typography2 component="p" css={styles.bodyText}>
            <FormattedMessage
              message={_t(
                'A verification email has been sent to {emailAddress}. After verification, please refresh or revisit this link to join the program.'
              )}
              emailAddress={
                <Typography2 component="strong" css={styles.strong}>
                  {emailAddress}
                </Typography2>
              }
            />
          </Typography2>
          {error && (
            <Typography2 component="p" color="error" css={styles.bodyText}>
              {_t('An error occurred, please try again later.')}
            </Typography2>
          )}
        </div>
      </EnrollmentChoiceModalBody>
    </div>
  );
};

const enhance = compose<Props, PropsFromCaller>(
  setDisplayName(getDisplayName(ProgramEmailVerifyModal)),
  connectToRouter((router) => ({ hasProductContext: useRouterParams(router) != null }))
);

export default enhance(ProgramEmailVerifyModal);
